'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';

type Status = 'idle' | 'loading' | 'success' | 'already_subscribed' | 'error';

export function NewsletterSignupCard() {
  const t = useTranslations('newsletter');
  const locale = useLocale();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const saveResponse = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language: locale }),
      });

      if (!saveResponse.ok) {
        if (saveResponse.status === 409) {
          setStatus('already_subscribed');
          return;
        }

        throw new Error('Newsletter save failed');
      }

      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_key: 'YOUR_NEWSLETTER_ACCESS_KEY_HERE',
            subject: t('subject'),
            email,
            language: locale,
            message: t('inbox_message', { email, locale }),
            autoresponse: t('autoresponse'),
          }),
        });

        if (!response.ok) {
          console.error('Newsletter Web3Forms submit failed.');
        }
      } catch {
        console.error('Newsletter Web3Forms submit failed.');
      }

      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="mt-6 rounded-3xl border border-blue-100 bg-gradient-to-br from-[#EFF6FF] via-white to-[#F8FAFF] p-6 shadow-sm sm:p-8">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0066FF]">{t('badge')}</p>
        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{t('title')}</h2>
        <p className="mt-3 text-slate-600">{t('description')}</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="newsletter-email" className="sr-only">
          {t('email_label')}
        </label>
        <input
          id="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('email_placeholder')}
          required
          disabled={status === 'loading'}
          className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-[#0066FF]"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="rounded-xl bg-[#0066FF] px-6 py-3 font-semibold text-white transition-all hover:bg-[#0052CC] disabled:cursor-not-allowed disabled:opacity-60 sm:whitespace-nowrap"
        >
          {status === 'loading' ? t('submitting') : t('submit')}
        </button>
      </form>

      {status === 'success' && (
        <p className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{t('success')}</p>
      )}

      {status === 'already_subscribed' && (
        <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('already_subscribed')}
        </p>
      )}

      {status === 'error' && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{t('error')}</p>
      )}
    </section>
  );
}